import Decimal from "decimal.js";
import { useCallback, useState } from "react";

type Location = {
  latitude: number;
  longitude: number;
};

type Place = {
  displayName: {
    text: string;
  };
  formattedAddress: string;
  id: string;
  location: Location;
  nationalPhoneNumber: string | null;
  websiteUri: string | null;
};

type NearbySearchRequest = {
  includedTypes?: string[];
  maxResultCount?: number;
  locationRestriction?: {
    circle: { center: Location; radius: number };
  };
  languageCode?: string;
  regionCode?: string;
  rankPreference?: "POPULARITY" | "DISTANCE";
};

export type SearchNearbyOptions = {
  radius?: number;
  includedTypes?: string[];
  maxResultCount?: number;
  rankPreference?: "POPULARITY" | "DISTANCE";
};

export const useNearbySearch = () => {
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(false);

  const searchNearby = useCallback(async (location: Location, options: SearchNearbyOptions = {}): Promise<Place[]> => {
    const { radius = 1000, includedTypes = [], maxResultCount = 20, rankPreference = "POPULARITY" } = options;

    setLoading(true);

    try {
      const requestBody: NearbySearchRequest = {
        locationRestriction: {
          circle: {
            center: location,
            radius: radius,
          },
        },
        maxResultCount,
        rankPreference,
      };

      if (includedTypes.length > 0) {
        requestBody.includedTypes = includedTypes;
      }

      const fieldMask = [
        "places.id",
        "places.displayName",
        "places.formattedAddress",
        "places.location",
        "places.nationalPhoneNumber",
        "places.websiteUri",
      ].join(",");

      const response = await fetch(`https://places.googleapis.com/v1/places:searchNearby`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": process.env["NX_PUBLIC_GOOGLE_MAPS_API_KEY"]!,
          "X-Goog-FieldMask": fieldMask,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API Error: ${response.status} - ${errorData.error?.message || "Unknown error"}`);
      }

      const data = await response.json();
      setPlaces(data.places || []);
      return data.places || [];
    } catch (err) {
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchNearby = useCallback(
    async (
      location: { latitude?: Decimal | number | null; longitude?: Decimal | number | null },
      options: SearchNearbyOptions,
    ) => {
      const lat = location.latitude;
      const lng = location.longitude;

      if (!lat || !lng) {
        setPlaces([]);
      }

      const locationToSearch: Location = {
        latitude: (lat instanceof Decimal ? lat.toNumber() : lat) as number,
        longitude: (lng instanceof Decimal ? lng.toNumber() : lng) as number,
      };

      return await searchNearby(locationToSearch, options);
    },
    [searchNearby],
  );

  return {
    places,
    loading,
    searchNearby: handleSearchNearby,
    setPlaces,
  };
};
